import { DatePipe, NgClass, NgTemplateOutlet, TitleCasePipe } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { Component, DestroyRef, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import moment from 'moment';
import { DeviceDetectorService } from 'ngx-device-detector';
import { Subscription } from 'rxjs';
import { environment } from '../../../../../environments/environment';
import { DATE_FORMAT, DATE_ISO_FORMAT } from '../../../../constants/date.constants';
import { TEMPLATE_CONSTANT } from '../../../../constants/template.constant';
import { TRANSLATION_TEMPLATES } from '../../../../constants/translation-templates-constants';
import { CustomFieldChildEvent, CustomFieldsDao } from '../../../../db-models/appointment-custom-fields-dao';
import { CouponDbModel } from '../../../../db-models/coupon-db.model';
import { CustomBookingMessageTemplate } from '../../../../db-models/widget-conf-dao';
import { EventCartItem, GuestModel } from '../../../../models/cart.model';
import { GlobalObjects, Partner } from '../../../../models/global';
import { EventState, UserState } from '../../../../models/state.model';
import { CustomEventService } from '../../../../services/custom-event.service';
import { CustomFieldService } from '../../../../services/custom-field.service';
import { DateUtilsService } from '../../../../services/date-utils.service';
import { FormsService } from '../../../../services/forms.service';
import { LocalStorageService } from '../../../../services/local-storage.service';
import { LoggerService } from '../../../../services/logger.service';
import { TimezoneService } from '../../../../services/timezone.service';
import { TaxableLine, VatResult, calculateIncludedVat, calculateVat, isVatAddedOnTop, vatTypeLabelKey } from '../../../../services/vat-calculation.util';
import { WidgetUtilService } from '../../../../services/widget-util.service';
import { NgxClampComponent } from '../../../../shared/components/ngx-clamp/ngx-clamp.component';
import { CalioCurrencyPipe } from '../../../../shared/pipes/calio-currency.pipe';
import { CalioMeetingTemplatePipe } from '../../../../shared/pipes/calio-meeting-template.pipe';
import { EventReplaceHourPipe } from '../../../../shared/pipes/event-replace-hour.pipe';
import { EventReplaceMinutePipe } from '../../../../shared/pipes/event-replace-minute.pipe';
import { IsEmptyStringPipe } from '../../../../shared/pipes/is-empty-string.pipe';
import { PricePipe } from '../../../../shared/pipes/price.pipe';
import { ReplaceCommaPipe } from '../../../../shared/pipes/replace-comma.pipe';
import { TranslationPipe } from '../../../../shared/pipes/translation.pipe';
import { TrustHtmlPipe } from '../../../../shared/pipes/trust-html.pipe';
import { CustomfieldsComponent } from '../../../common/customfields/customfields.component';
import { CwCardHeaderComponent } from '../../../common/theme/cw-card-header/cw-card-header.component';
import { CwNewCardComponent } from '../../../common/theme/cw-new-card/cw-new-card.component';

const pipes = [EventReplaceHourPipe, EventReplaceMinutePipe, PricePipe, ReplaceCommaPipe, DatePipe, TitleCasePipe, TranslationPipe, CalioCurrencyPipe, IsEmptyStringPipe, CalioMeetingTemplatePipe, TrustHtmlPipe];
const components = [CwNewCardComponent, CwCardHeaderComponent, NgxClampComponent, CustomfieldsComponent];
const modules = [TranslateModule];
const common = [NgClass, NgTemplateOutlet];

@Component({
  selector: 'app-event-cart-overview',
  templateUrl: './event-cart-overview.component.html',
  styleUrls: ['./event-cart-overview.component.scss'],
  imports: [...modules, ...common, ...pipes, ...components]
})
export class EventCartOverviewComponent implements OnInit, OnChanges {
  protected readonly vatTypeLabelKey = vatTypeLabelKey;

  @Input() additionalGuests: GuestModel[] = [];
  @Input() cart: EventCartItem[];
  @Input() lang: string;
  @Input() partner: Partner;
  @Input() totalCartDuration: number;
  @Input() set totalCartPrice(value: number) {
    this._totalCartPrice = value;
  }
  @Input() set customFieldsAddonsPrice(value: number) {
    this._customFieldsAddonsPrice = value;
  }
  @Input() cartLsName: string;
  @Input() coupon: CouponDbModel;
  @Input() courtesyForm: string;
  @Input() finalCouponDiscount: number = Number(0);
  @Input() eventState: EventState;
  @Input() widgetTemplates: CustomBookingMessageTemplate[] = [];
  @Input() userState: UserState;
  @Input() customFields: CustomFieldsDao[];
  @Input() selectedJsonCustomFields: {
    [key: number]: { type: string; value: any } | any;
  };
  @Input() customFieldValues: {
    [key: number]: {
      type: string, value: any, option_values: {
        custom_field_option_id: number, custom_field_option_value: string,
      }[]
    } | any
  };
  @Input() customFieldValuesLSNameKey: string;
  @Input() globals: GlobalObjects;
  @Input() anonymousGuestCount = 0;

  /**
   * Combined additional-guest count for the cart summary line. Named guests
   * and anonymous-count guests are shown as a single "N additional guests"
   * row; the per-type distinction is already visible in the participant cards
   * above. Keeps the subtotal block clean even in 'both' mode.
   */
  protected get combinedGuestCount(): number {
    return (this.additionalGuests?.length ?? 0) + (this.anonymousGuestCount ?? 0);
  }

  @Output() removeFromCartEvent = new EventEmitter<string>();

  private customEventService = inject(CustomEventService);
  private customFieldService = inject(CustomFieldService);
  private dateUtilsService = inject(DateUtilsService);
  private destroyRef = inject(DestroyRef);
  private deviceDetectorService = inject(DeviceDetectorService);
  private formsService = inject(FormsService);
  private localStorageService = inject(LocalStorageService);
  private timezoneService = inject(TimezoneService);
  private translateService = inject(TranslateService);
  private widgetUtilService = inject(WidgetUtilService);

  readonly dateFormat = DATE_FORMAT;
  readonly dateIsoFormat = DATE_ISO_FORMAT;
  readonly environment = environment;
  readonly templateContent = TRANSLATION_TEMPLATES;

  get timeFormat(): string {
    return this.dateUtilsService.userTimeFormat.date;
  }

  get datePipeWithComma(): string {
    return this.dateUtilsService.userDateTimeWithCommaFormat;
  }

  get dateTimeFormat(): string {
    return this.dateUtilsService.userDateTimeFormat.date;
  }

  templateTitle: CustomBookingMessageTemplate;
  templateSubTitle: CustomBookingMessageTemplate;
  templateAddonUpgradeLabel: CustomBookingMessageTemplate;
  isMobile: boolean;
  selectedChildEvent: CustomFieldChildEvent;
  selectedChildEventLabel: string;
  toggleCustomFieldChildEventSubscription: Subscription;
  finalTotal: number;
  showWidgetSlotLocation = this.widgetUtilService.getWidgetConf().show_widget_slot_location;
  protected _totalCartPrice: number;
  protected _customFieldsAddonsPrice: number;
  protected readonly isVatAddedOnTop = isVatAddedOnTop;
  /** VAT summary (one entry per rate) on the discounted, participant-multiplied net. */
  vat = signal<VatResult>({ byRate: [], totalCents: 0 });
  /** VAT already contained in the prices (one entry per rate) — `inclusive` partners only, display only. */
  containedVat = signal<VatResult>({ byRate: [], totalCents: 0 });

  constructor() {
    this.translateService.onLangChange.subscribe(language => this.lang = language.lang);
  }

  ngOnInit(): void {
    this.isMobile = this.deviceDetectorService.isMobile();

    this.toggleCustomFieldChildEventSubscription = this.customFieldService.toggleCustomFieldChildEvent
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(result => {
        this.selectedChildEvent = result.childEvent;
        this.selectedChildEventLabel = result.label;

        if (this.selectedChildEvent) {
          this.getSlots();
        } else {
          this.selectedChildEvent = undefined;
          this.selectedChildEventLabel = undefined;
          this.resetCartItemsToInitialState();
        }
      });
  }

  removeFromCart(slotId: any): void {
    this.removeFromCartEvent.next(slotId);
  }

  async getSlots(): Promise<void> {
    for (const cart of this.cart) {
      await new Promise(resolve => {
        const date = moment(cart.date).format(this.dateIsoFormat);
        this.formsService.getSlots(
          this.selectedChildEvent.uuid,
          date,
          'day',
          this.partner.is_timezone_enabled ? (this.userState?.timezone ?? this.timezoneService.localTimezone) : undefined
        )
          .pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe({
            next: dates => {
              if (dates?.length) {
                const eventSlot = dates[0];
                const optionsCount = eventSlot.regular_capacity - eventSlot.regular_booking_count;
                if (optionsCount > 0) {
                  const tempSlotOptions = Array.from(Array(optionsCount), (_, x) => x + 1);
                  cart.tempSelectOptionLabel = this.selectedChildEvent.title;
                  cart.tempSelectLabel = this.selectedChildEventLabel;
                  cart.tempSlotOptions = tempSlotOptions;
                  if (!cart.selectedSlot) {
                    cart.selectedSlot = {
                      slot_id: eventSlot.id,
                      amount: tempSlotOptions[0]
                    };
                  }
                }
              }

              resolve(null);
            },
            error: (error: HttpErrorResponse) => LoggerService.error(error)
          });
      });
    }

    this.removeCartFromLocalStorage();
  }

  removeCartFromLocalStorage() {
    this.localStorageService.removeItem(this.cartLsName, () => this.updateCartInLocalStorage());
  }

  updateCartInLocalStorage(): void {
    this.localStorageService.setItem(this.cartLsName, this.cart, () => void (0));
  }

  resetCartItemsToInitialState(): void {
    for (const cart of this.cart) {
      cart.tempSelectOptionLabel = undefined;
      cart.tempSelectLabel = undefined;
      cart.tempSlotOptions = undefined;
      cart.selectedSlot = undefined;
    }
  }

  getFreeOrHidden(): string {
    for (const cart of this.cart) {
      if (cart.currentEvent.price === 0) {
        return 'free';
      }
    }

    return '';
  }

  getFinalTotal(): number {
    if (this._totalCartPrice > 0) {
      const totalParticipants = 1
        + (this.additionalGuests?.length ?? 0)
        + (this.anonymousGuestCount ?? 0);
      let netAfterCoupon = (totalParticipants * Number(this._totalCartPrice)) - Number(this.finalCouponDiscount);
      if (netAfterCoupon <= 0) {
        netAfterCoupon = 0;
      }
      const vatResult = this.calculateCartVat(netAfterCoupon, totalParticipants);
      this.vat.set(vatResult);
      this.containedVat.set(this.calculateCartVat(netAfterCoupon, totalParticipants, true));
      this.finalTotal = Number((netAfterCoupon + vatResult.totalCents / 100).toFixed(2));
      return this.finalTotal;
    } else {
      this.vat.set({ byRate: [], totalCents: 0 });
      this.containedVat.set({ byRate: [], totalCents: 0 });
      this.finalTotal = 0;
      return this.finalTotal;
    }
  }

  protected subtotalLabelKey(): string {
    return this.showsVatRows() ? 'tax_info.net_total' : 'summary_page_translations.subtotal';
  }

  protected totalLabelKey(): string {
    if (vatTypeLabelKey(this.partner?.widget_vat_type) === 'exclude') {
      return 'tax_info.amount_excl_tax';
    }
    return this.showsVatRows() ? 'tax_info.amount_incl_tax' : 'appointments.step4.total';
  }

  /** The VAT wording only applies when VAT is added on top or the prices actually contain some. */
  private showsVatRows(): boolean {
    return isVatAddedOnTop(this.partner?.widget_vat_type) || this.containedVat().byRate.length > 0;
  }

  /**
   * Net-after-coupon (participant-multiplied) → VAT: on top for `plus`, or with `contained` the
   * share the prices already hold for `inclusive`. Empty/zero otherwise.
   */
  private calculateCartVat(discountedNet: number, totalParticipants: number, contained = false): VatResult {
    const vatType = this.partner?.widget_vat_type;
    if (contained ? vatTypeLabelKey(vatType) !== 'inclusive' : !isVatAddedOnTop(vatType)) {
      return { byRate: [], totalCents: 0 };
    }
    const lines: TaxableLine[] = (this.cart || []).reduce((acc, item) => {
      const price = item.currentEvent?.price || 0;
      const rate = item.currentEvent?.taxRate || 0;
      if (price > 0 && rate > 0) {
        acc.push({ netCents: Math.round(price * 100 * totalParticipants), rateHundredths: Math.round(rate * 100) });
      }
      return acc;
    }, [] as TaxableLine[]);
    const discountedNetCents = Math.round(discountedNet * 100);
    const undiscountedNetCents = Math.round(totalParticipants * Number(this._totalCartPrice) * 100);
    return (contained ? calculateIncludedVat : calculateVat)(lines, discountedNetCents, undiscountedNetCents);
  }

  /** Per-item `+ amount (plus X% VAT)` row — ratio 1 (no cross-item coupon allocation here). */
  getItemVatAmount(rate: number, itemNet: number): number {
    if (!isVatAddedOnTop(this.partner?.widget_vat_type) || !(itemNet > 0) || !(rate > 0)) {
      return 0;
    }
    const itemNetCents = Math.round(itemNet * 100);
    const result = calculateVat([{ netCents: itemNetCents, rateHundredths: Math.round(rate * 100) }], itemNetCents, itemNetCents);
    return result.totalCents / 100;
  }

  onAdditionalGuestChange(): void {
    const change = {
      finalPrice: this.finalTotal,
      discount: this.finalCouponDiscount,
      additionalGuests: this.additionalGuests,
    };
    this.customEventService.lastAdditionalGuestChange = change;
    this.customEventService.additionalGuestChangeEvent.emit(change);
  }

  ngOnChanges(changes: SimpleChanges): void {
    this.customFieldService.addonsCustomFieldsPriceEvent.subscribe(() => {
      this.getFinalTotal();
      this.onAdditionalGuestChange();
    });
    this.getFinalTotal();
    this.onAdditionalGuestChange();

    if (changes?.widgetTemplates?.currentValue) {
      this.widgetTemplates?.length && this.setupTemplates();
    }
  }

  setupTemplates(): void {
    this.templateTitle = this.widgetTemplates.find(template => template.id === 124);
    this.templateTitle && (this.templateTitle.is_multi_language = 1);

    this.templateSubTitle = this.widgetTemplates.find(template => template.id === 125);
    this.templateSubTitle && (this.templateSubTitle.is_multi_language = 1);

    this.templateAddonUpgradeLabel = this.widgetTemplates.find(template => template.id === TEMPLATE_CONSTANT.ADDON_UPGRADE_LABEL);
    this.templateAddonUpgradeLabel && (this.templateAddonUpgradeLabel.is_multi_language = 1);
  }
}

